import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Image } from 'antd'
import { Icon } from '@iconify/react'
import dayjs from 'dayjs'

import {
    fetchWorkloadScreenshots,
    workloadState,
} from '../workload.slice'

import styles from './WorkloadScreenshotsGallery.module.css'

const getScrollParent = (element) => {
    let node = element?.parentElement
    while (node) {
        const { overflowY } = window.getComputedStyle(node)
        if (overflowY === 'auto' || overflowY === 'scroll') return node
        node = node.parentElement
    }
    return null
}

const WorkloadScreenshotsGallery = () => {
    const dispatch = useDispatch()
    const { detail } = useSelector(workloadState)
    const screenshots = detail?.screenshots || {}
    const {
        status = 'idle',
        error = '',
        rows = [],
        isNext = false,
    } = screenshots

    const isLoading = status === 'loading'
    const isLoadingMore = status === 'loadingMore'
    const isError = status === 'error'

    const sentinelRef = useRef(null)
    const loadingMoreRef = useRef(false)

    useEffect(() => {
        loadingMoreRef.current = isLoadingMore
    }, [isLoadingMore])

    useEffect(() => {
        const sentinel = sentinelRef.current
        if (!sentinel || !isNext) return undefined

        const scrollRoot = getScrollParent(sentinel)
        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return
                if (loadingMoreRef.current) return
                dispatch(fetchWorkloadScreenshots({ reset: false }))
            },
            {
                root: scrollRoot,
                rootMargin: '120px 0px',
                threshold: 0,
            }
        )

        observer.observe(sentinel)
        return () => observer.disconnect()
    }, [dispatch, isNext, rows.length])

    const handleRetry = () => {
        dispatch(fetchWorkloadScreenshots({ reset: true }))
    }

    if (isLoading) {
        return (
            <div className={styles.loadingWrap}>
                <Icon
                    icon="line-md:loading-loop"
                    style={{ fontSize: 40, color: '#6366f1' }}
                />
                <p className={styles.loadingText}>Loading screenshots…</p>
            </div>
        )
    }

    if (isError) {
        return (
            <div className={styles.stateCard}>
                <div className={styles.stateIcon} aria-hidden>
                    <Icon icon="mdi:image-off-outline" width={22} height={22} />
                </div>
                <p className={styles.stateTitle}>Couldn’t load screenshots</p>
                <p className={styles.stateText}>
                    {error || 'Something went wrong. Please try again.'}
                </p>
                <button
                    type="button"
                    className={styles.retryBtn}
                    onClick={handleRetry}
                >
                    <Icon icon="mdi:refresh" width={16} />
                    Try again
                </button>
            </div>
        )
    }

    if (!rows?.length) {
        return (
            <div className={styles.stateCard}>
                <div className={styles.stateIcon} aria-hidden>
                    <Icon
                        icon="mdi:monitor-screenshot"
                        width={22}
                        height={22}
                    />
                </div>
                <p className={styles.stateTitle}>No screenshots</p>
                <p className={styles.stateText}>
                    No captures for this member in the selected period.
                </p>
            </div>
        )
    }

    return (
        <div className={styles.gallery}>
            <div className={styles.galleryMeta}>
                <span className={styles.galleryCount}>
                    {rows.length} screenshot{rows.length === 1 ? '' : 's'}
                    {isNext ? '+' : ''}
                </span>
                <span className={styles.galleryHint}>Click to enlarge</span>
            </div>

            <Image.PreviewGroup>
                <div className={styles.grid}>
                    {rows.map((shot) => {
                        const key = shot?._id || shot?.url
                        const capturedAt = shot?.createdAt
                            ? dayjs(shot.createdAt).format('D MMM · h:mm A')
                            : '—'
                        const windowTitle =
                            shot?.activeWindow?.title ||
                            shot?.activeWindow?.app ||
                            ''

                        return (
                            <figure key={key} className={styles.card}>
                                <div className={styles.thumbWrap}>
                                    <Image
                                        src={shot?.url}
                                        alt={windowTitle || 'Screenshot'}
                                        className={styles.thumb}
                                        preview={{
                                            mask: (
                                                <span
                                                    className={
                                                        styles.previewMask
                                                    }
                                                >
                                                    <Icon
                                                        icon="mdi:magnify-plus-outline"
                                                        width={22}
                                                    />
                                                    View
                                                </span>
                                            ),
                                        }}
                                    />
                                </div>
                                <figcaption className={styles.caption}>
                                    <span className={styles.captionTime}>
                                        <Icon
                                            icon="mdi:clock-outline"
                                            width={13}
                                        />
                                        {capturedAt}
                                    </span>
                                    {(shot?.total_mouse_movements > 0 ||
                                        shot?.total_keypresses > 0) && (
                                        <span className={styles.captionStats}>
                                            {shot?.total_mouse_movements >
                                            0 ? (
                                                <span title="Mouse activity">
                                                    <Icon
                                                        icon="mdi:mouse"
                                                        width={12}
                                                    />
                                                    {shot.total_mouse_movements}
                                                </span>
                                            ) : null}
                                            {shot?.total_keypresses > 0 ? (
                                                <span title="Keyboard activity">
                                                    <Icon
                                                        icon="mdi:keyboard-outline"
                                                        width={12}
                                                    />
                                                    {shot.total_keypresses}
                                                </span>
                                            ) : null}
                                        </span>
                                    )}
                                    {windowTitle ? (
                                        <span
                                            className={styles.captionWindow}
                                            title={windowTitle}
                                        >
                                            {windowTitle}
                                        </span>
                                    ) : null}
                                </figcaption>
                            </figure>
                        )
                    })}
                </div>
            </Image.PreviewGroup>

            {isNext ? (
                <div
                    ref={sentinelRef}
                    className={styles.infiniteSentinel}
                    aria-hidden
                >
                    {isLoadingMore ? (
                        <span className={styles.infiniteLoading}>
                            <Icon
                                icon="line-md:loading-loop"
                                width={18}
                                height={18}
                            />
                            Loading more…
                        </span>
                    ) : null}
                </div>
            ) : null}
        </div>
    )
}

export default WorkloadScreenshotsGallery
